import { z } from "zod";
import { AppPermission } from "./auth.schema.ts";

export const AppAbilityActionSchema = z.enum(["read", "use", "manage"]);
export const AppAbilitySubjectSchema = z.union([
  AppPermission,
  z.literal("all"),
]);

const _conditions = z.record(z.string(), z.unknown()).nullable();

// manage is only valid with subject "all" (CASL super-admin wildcard)
const _ManagePermission = z.object({
  action: z.literal("manage"),
  subject: z.literal("all"),
  conditions: _conditions,
  inverted: z.boolean(),
});

// use and read are only valid with specific AppPermission subjects
const _UsePermission = z.object({
  action: z.literal("use"),
  subject: AppPermission,
  conditions: _conditions,
  inverted: z.boolean(),
});

const _ReadPermission = z.object({
  action: z.literal("read"),
  subject: AppPermission,
  conditions: _conditions,
  inverted: z.boolean(),
});

export const PermissionRule = z.discriminatedUnion("action", [
  _ManagePermission,
  _UsePermission,
  _ReadPermission,
]);
export type PermissionRule = z.infer<typeof PermissionRule>;

export const CustomerRoleDto = z.object({
  id: z.string().uuid(),
  customerId: z.string().uuid(),
  name: z.string().min(1),
  description: z.string().nullable(),
  isSystem: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  isActive: z.boolean(),
  deactivatedAt: z.string().nullable(),
  deactivatedBy: z.string().uuid().nullable(),
});

export type CustomerRoleDto = z.infer<typeof CustomerRoleDto>;

const _dtoBase = {
  id: z.string().uuid(),
  roleId: z.string().uuid(),
  documentTypeId: z.string().uuid().nullable(),
  conditions: z.record(z.string(), z.unknown()).nullable(),
  inverted: z.boolean(),
};

export const RolePermissionDto = z.discriminatedUnion("action", [
  z.object({
    ..._dtoBase,
    action: z.literal("manage"),
    subject: z.literal("all"),
  }),
  z.object({ ..._dtoBase, action: z.literal("use"), subject: AppPermission }),
  z.object({ ..._dtoBase, action: z.literal("read"), subject: AppPermission }),
]);

export type RolePermissionDto = z.infer<typeof RolePermissionDto>;

export const CreateCustomerRoleInput = z.object({
  customerId: z.string().uuid(),
  name: z.string().min(1),
  description: z.string().optional(),
  isSystem: z.boolean().optional(),
});

export type CreateCustomerRoleInput = z.infer<typeof CreateCustomerRoleInput>;

const _inputBase = {
  roleId: z.string().uuid(),
  documentTypeId: z.string().uuid().optional(),
  conditions: z.record(z.string(), z.unknown()).optional(),
  inverted: z.boolean().optional(),
};

export const AddRolePermissionInput = z.discriminatedUnion("action", [
  z.object({
    ..._inputBase,
    action: z.literal("manage"),
    subject: z.literal("all"),
  }),
  z.object({ ..._inputBase, action: z.literal("use"), subject: AppPermission }),
  z.object({
    ..._inputBase,
    action: z.literal("read"),
    subject: AppPermission,
  }),
]);

export type AddRolePermissionInput = z.infer<typeof AddRolePermissionInput>;

export const DocumentTypePermissionRule = z.object({
  documentTypeId: z.string().uuid(),
  /** "none" removes any existing rule; "read" grants read; "write" grants read+write */
  level: z.enum(["none", "read", "write"]),
});
export type DocumentTypePermissionRule = z.infer<
  typeof DocumentTypePermissionRule
>;

export const RolePermissionsBody = z.object({
  permissions: z.array(AppPermission),
  /** Per-document-type access rules for the `requests` module. */
  documentTypeRules: z.array(DocumentTypePermissionRule).optional(),
  /** Human-readable role description (customer_roles.description). */
  description: z.string().nullable().optional(),
});

export type RolePermissionsBody = z.infer<typeof RolePermissionsBody>;

// BEL-460: bulk-fetch response for the role list — a map of roleId ->
// AppPermission[], batching what used to be N per-role requests
// (rolePermissionsQueries.forRole) into one (rolePermissionsQueries.forRoles).
export const BulkRolePermissionsBody = z.record(
  z.string().uuid(),
  z.array(AppPermission),
);
export type BulkRolePermissionsBody = z.infer<typeof BulkRolePermissionsBody>;

// BEL-454: role-type entities must be created/renamed exclusively via the
// role service (createCustomerRole, one transaction, shared id with
// fsm_entities) — never via the generic entity-admin endpoint. `name` is
// optional on the update body so a rename can ride along with a permissions
// save without a second round-trip to the (now-blocked) generic endpoint.
export const UpdateCustomerRoleBody = RolePermissionsBody.extend({
  name: z.string().min(1).optional(),
});
export type UpdateCustomerRoleBody = z.infer<typeof UpdateCustomerRoleBody>;

export const CreateCustomerRoleBody = RolePermissionsBody.extend({
  name: z.string().min(1),
});
export type CreateCustomerRoleBody = z.infer<typeof CreateCustomerRoleBody>;

export const CreateCustomerRoleResponse = RolePermissionsBody.extend({
  id: z.string().uuid(),
  name: z.string(),
});
export type CreateCustomerRoleResponse = z.infer<
  typeof CreateCustomerRoleResponse
>;
